import * as React from 'react'

import { Box, Icon, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { dateToString, isCurrentYear } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import InitialConditions from 'modules/contracts/components/ContractHistory/InitialConditions'
import { Milestone } from 'modules/contracts/components/ContractHistory/Timeline/Milestone'
import { getContractType } from 'modules/contracts/components/ContractHistory/lib/getContractType'
import { getMilestoneColor } from 'modules/contracts/components/ContractHistory/lib/timeline'
import { ContractSchedule } from 'modules/contracts/lib/contractSchedule'
import { useLegalEntityContractData } from 'modules/contracts/lib/legalEntityContractDataContext'
import type ContractVersion from 'modules/contracts/store/models/ContractVersion'

import MetadataInfo from './MetadataInfo'
import StatusTag from './StatusTag'

interface Props {
  contractVersion: ContractVersion
  contractVersionSchedule: string
  isHighlighted: boolean
  showChanges: boolean
  hasPreviousContract: boolean
}

const FirstContractVersionCard = ({
  contractVersion,
  contractVersionSchedule,
  isHighlighted,
  showChanges,
  hasPreviousContract,
}: Props) => {
  const legalEntityContractData = useLegalEntityContractData()
  const showScheduleTag = contractVersionSchedule !== ContractSchedule.expired
  const contractType = getContractType(contractVersion, legalEntityContractData)
  const createdAt = contractVersion.createdAtString
  const authorAvatarUrl = contractVersion.authorAvatarUrl
  const authorName = contractVersion.authorName

  const getContractTitle = (contractType: string) => {
    if (hasPreviousContract) {
      if (contractType === '') return i18n.t('contract_versions.navigator.timeline.rehired')

      return i18n.t('contract_versions.navigator.timeline.rehired_with_type', {
        contractType: contractType,
      })
    }

    if (contractType === '') return i18n.t('contract_versions.navigator.timeline.hired')

    return i18n.t('contract_versions.navigator.timeline.hired_with_type', {
      contractType: contractType,
    })
  }
  const date = isCurrentYear(contractVersion.effectiveOn)
    ? dateToString(contractVersion.effectiveOn, 'ui_dm_long')
    : dateToString(contractVersion.effectiveOn, 'ui_dmy_long')

  const contractTitle = getContractTitle(contractType)
  return (
    <Box gap="s2" width="full">
      <Milestone color={getMilestoneColor(contractVersionSchedule)} label={date} extraPadding={17}>
        <Box flexDirection="column" width="full" gap="s8">
          <Box flexDirection="row" alignItems="flexStart" justifyContent="spaceBetween" gap="s8">
            <Box flexDirection="row" alignItems="center" gap="s8">
              <Icon.Small icon={showChanges ? 'chevron-up' : 'chevron-down'} color="grey800" />
              <Text.Normal weight="semibold" size="200" color="grey1000">
                {contractTitle}
              </Text.Normal>
            </Box>
            {showScheduleTag && (
              <StatusTag schedule={contractVersionSchedule} isHighlighted={isHighlighted} />
            )}
          </Box>
          {authorName && (
            <MetadataInfo
              authorName={authorName}
              createdAt={createdAt}
              authorAvatarUrl={authorAvatarUrl}
            />
          )}
        </Box>
      </Milestone>
      {showChanges && <InitialConditions contractVersion={contractVersion} />}
    </Box>
  )
}

export default observer(FirstContractVersionCard)
